import { isAfter, parseISO } from 'date-fns';
import { isNil } from 'lodash-es';
import { computed, readonly, ref, watch } from 'vue';

import { setRefreshTime } from '@/composables/useGlobalRefresh';
import { usePostMessageReceiver } from '@/composables/usePostMessageReceiver';
import { useTraceScheduledPostBug } from '@/composables/useTraceScheduledPostBug';
import { ARCHIVE_TYPE } from '@/store/constants';
import { findNextScheduledPost } from '@/utils/findNextScheduledPost';
import { clarifyObject } from '@/utils/objectHelper';

import type { GeneralTemplateData, GeneralTemplateHandsupConfig, HandsupConfig, Product, RealtimeServiceProvider, TemplateData} from '../js/api';
import { IS_GENERAL_CMS, LIVE_STATUS } from '../js/constants';
import { loadLocaleMessages } from '../plugins/i18n';

const templateData = ref<GeneralTemplateData | TemplateData | null>(null);

const setTemplateData = (payload: GeneralTemplateData | TemplateData) => {
  templateData.value = payload;
};

const handsupConfig = computed<HandsupConfig | GeneralTemplateHandsupConfig | null>(() => {
  return templateData.value?.handsup_config ?? null;
});

watch(handsupConfig, () => {
  // setup locale
  const locale = String(handsupConfig.value?.store?.locale ?? '');
  loadLocaleMessages(locale);
});

const realtimeServiceProvider = computed<RealtimeServiceProvider | null>(() => {
  const config = templateData.value?.handsup_config?.store;
  return (config && 'realtime_service_provider' in config) ? config.realtime_service_provider : null;
});

const layoutContent = computed(() => {
  try {
    const layoutContentStr = templateData.value?.layout_content;
    if (!layoutContentStr) {
      return {};
    }
    return JSON.parse(layoutContentStr);
  } catch {
    return {};
  }
});

const visibleModuleList = computed(() => {
  return layoutContent.value?.modules?.filter((module: { isVisible: boolean }) => module.isVisible) ?? [];
});

const productList = ref<Product[]>([]);
// from rawData
watch(templateData, (data) => {
  productList.value = data?.product_list ?? [];
});
// 監聽 商品變更、商品狀態更新
const { onProductChanged, onProductSalableStatus } = usePostMessageReceiver();
onProductChanged((products: Product[]) => {
  productList.value = products;
});
onProductSalableStatus((updateProducts: { product_id: number; salable_status: string }[]) => {
  productList.value = productList.value.map((p) => {
    const matchProduct = updateProducts.find((u) => u.product_id === (p as any).id);
    if (matchProduct) { (p as any).salable_status = matchProduct.salable_status; }

    return p;
  });
});

const upcomingPostList = computed(() => {
  return templateData.value?.upcoming_post_list ?? [];
});

const liveHistoryList = computed(() => {
  return templateData.value?.live_history_list ?? [];
});

const scheduledPostList = computed(() => {
  const data = templateData.value;
  return (data && 'scheduled_post_list' in data) ? data.scheduled_post_list ?? [] : [];
});

const { notify } = useTraceScheduledPostBug('API::watch(scheduledPostList)');
watch(scheduledPostList, () => {
  const postId = handsupConfig.value?.post?.id ?? null;
  const nextScheduledPost = findNextScheduledPost(postId, scheduledPostList.value);
  if (!isNil(nextScheduledPost)) {
    notify({
      postId,
      nextScheduledPost,
    });

    setRefreshTime(parseISO(nextScheduledPost.scheduled_started_at).getTime());
  }
});

const currentStreamStatus = computed(() => {
  return templateData.value?.current_stream_status ?? null;
});

const liveStatus = computed(() => {
  const publications = currentStreamStatus.value?.publications;
  const publication = publications?.find((p: { channel: { type: string }; live_status: string }) => p.channel.type === 'handsup') ??
    publications?.[0];

  if (publication?.live_status === 'unpublished') {
    return LIVE_STATUS.UNPUBLISHED;
  }

  if (publication?.live_status === 'live') {
    return LIVE_STATUS.LIVE;
  }

  const replayExpiredAt = currentStreamStatus.value?.replay_expired_at;
  const isReplayNotExpired = !isNil(replayExpiredAt) && isAfter(parseISO(replayExpiredAt), Date.now());
  const isCaptionReady = currentStreamStatus.value?.is_handsup_caption_ready ?? false;
  const isReplayVideoReady = currentStreamStatus.value?.stream_info?.replay_url ?? currentStreamStatus.value?.stream_info?.replay_url_mp4 ?? false;

  if (publication?.live_status === 'live_stopped' &&
    isReplayNotExpired &&
    (!isCaptionReady || !isReplayVideoReady)
  ) { return LIVE_STATUS.WAIT_REPLAY_READY; }

  if (publication?.live_status === 'live_stopped' &&
    isReplayNotExpired &&
    isCaptionReady &&
    isReplayVideoReady
  ) { return LIVE_STATUS.REPLAY; }

  if (publication?.live_status === 'live_stopped' && !isReplayNotExpired) {
    return LIVE_STATUS.END;
  }

  console.error(`Unexpected live status: ${JSON.stringify(currentStreamStatus.value)}`);
  return LIVE_STATUS.UNKNOWN;
});

const isUpcoming = computed(() => {
  return liveStatus.value === LIVE_STATUS.UNPUBLISHED;
});

const isOnLive = computed(() => {
  return liveStatus.value === LIVE_STATUS.LIVE;
});

const isWaitReplayReady = computed(() => {
  return liveStatus.value === LIVE_STATUS.WAIT_REPLAY_READY;
});

const isReplaying = computed(() => {
  return liveStatus.value === LIVE_STATUS.REPLAY;
});

const isClipArchive = computed(() => {
  return currentStreamStatus.value?.archive?.type === ARCHIVE_TYPE.CLIP;
});

const isGeneralCmsStreamCanPlay = computed(() => {
  if (!IS_GENERAL_CMS) { return true; }

  if (isNil(handsupConfig.value?.post)) { return false; }

  return isUpcoming.value || isOnLive.value || isWaitReplayReady.value || isReplaying.value;
});

const isModuleIncludingStreamRoom = computed(() => {
  const streamRoomModules = [
    'StreamRoom',
    'StreamRoomAndProduct',
  ];

  return visibleModuleList.value.some((module: { name: string }) => {
    return streamRoomModules.includes(module.name);
  });
});

const isModuleIncludingUpcomingLiveFab = computed(() => {
  const upcomingLiveFabModule = 'UpcomingLiveFab';

  return visibleModuleList.value.some((module: { name: string }) => module.name === upcomingLiveFabModule);
});

// hook style
export const useTemplate = () => ({
  templateData: readonly(templateData),
  setTemplateData,
  visibleModuleList,
  productList,
  upcomingPostList,
  liveHistoryList,
  handsupConfig,
  realtimeServiceProvider,

  currentStreamStatus,
  scheduledPostList,

  isUpcoming,
  isOnLive,
  isReplaying,
  isClipArchive,

  isGeneralCmsStreamCanPlay,
  isModuleIncludingStreamRoom,
  isModuleIncludingUpcomingLiveFab,
});

const globalStyle = computed(() => layoutContent.value?.globalStyle);

const titleStyle = computed(() => clarifyObject({
  fontSize: globalStyle.value?.title?.textSize,
  lineHeight: globalStyle.value?.title?.lineHeight,
  color: globalStyle.value?.title?.textColor,
}));

const subtitleStyle = computed(() => clarifyObject({
  fontSize: globalStyle.value?.subtitle?.textSize,
  lineHeight: globalStyle.value?.subtitle?.lineHeight,
  color: globalStyle.value?.subtitle?.textColor,
}));

const descriptionStyle = computed(() => clarifyObject({
  fontSize: globalStyle.value?.description?.textSize,
  lineHeight: globalStyle.value?.description?.lineHeight,
  color: globalStyle.value?.description?.textColor,
}));

export const useGlobalStyle = () => ({
  titleStyle,
  subtitleStyle,
  descriptionStyle,
});
